import styled, { keyframes } from 'styled-components';
import { tokens, orange } from 'components/v3';

export const ContentCard = styled.div`
  background: ${({ theme }) => tokens(theme).card};
  border: 1px solid ${({ theme }) => tokens(theme).border};
  border-radius: 16px;
  box-shadow: ${({ theme }) => tokens(theme).shadowCard};
  padding: 30px;
  padding-bottom: 60px; /* Ensure whitespace at bottom */

  @media (max-width: 768px) {
    padding: 12px;
    padding-bottom: 40px;
  }
`;

export const ContentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const SearchWrapper = styled.div`
  display: flex;
  flex: 1;
  max-width: 340px;

  @media (max-width: 768px) {
    max-width: none;
  }
`;

export const VideosSection = styled.div`
  margin-bottom: 40px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

export const SectionTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const SectionInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => tokens(theme).text};
  margin: 0;
`;

export const SectionDescription = styled.p`
  font-size: 14px;
  color: ${({ theme }) => tokens(theme).sub};
  margin: 0;
`;

export const SeeAllButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => tokens(theme).primary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 0.2s;

  &:hover {
    background: ${orange(0.09)};
  }
`;

export const VideosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const WhatsNewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    display: flex;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 8px;

    & > * {
      flex: 0 0 85%;
      scroll-snap-align: start;
    }
  }
`;

export const TopicFiltersWrapper = styled.div`
  margin-bottom: 24px;
`;

export const WhatsNewContainer = styled.div`
  margin-bottom: 40px;
  padding: 28px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => tokens(theme).border};
  background: ${({ theme }) => tokens(theme).inset};

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export const WhatsNewHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

export const WhatsNewIcon = styled.div`
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 12px;
  background: ${({ theme }) => tokens(theme).primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

export const WhatsNewInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const WhatsNewTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => tokens(theme).text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const NewBadge = styled.span`
  padding: 4px 10px;
  background: ${({ theme }) => tokens(theme).primary};
  color: white;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
`;

export const WhatsNewDescription = styled.p`
  font-size: 14px;
  color: ${({ theme }) => tokens(theme).sub};
  margin: 0;
`;

/* ===========================================================
   Empty State — "Spotlight glow" (Learning Hub)
   =========================================================== */

const pulseRing = keyframes`
  0% {
    transform: scale(0.7);
    opacity: 0.9;
  }
  100% {
    transform: scale(1.25);
    opacity: 0;
  }
`;

export const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 64px 24px;

  @media (max-width: 768px) {
    padding: 48px 16px;
  }
`;

export const EmptyGlowWrap = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const EmptyRing = styled.span`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid
    ${({ theme }) => orange(theme?.name === 'dark' ? 0.28 : 0.16)};
  animation: ${pulseRing} 2.6s ease-out infinite;

  &:nth-child(2) {
    animation-delay: 1.3s;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.6;
  }
`;

export const EmptyCore = styled.div`
  position: relative;
  width: 84px;
  height: 84px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    ${({ theme }) => tokens(theme).primary},
    #ff7a4d
  );
  color: #ffffff;
  box-shadow: 0 10px 30px ${orange(0.4)};
`;

export const EmptyTitle = styled.h3`
  margin: 22px 0 8px;
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => tokens(theme).text};
`;

export const EmptyDescription = styled.p`
  margin: 0;
  max-width: 420px;
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => tokens(theme).sub};
`;

export const EmptyAction = styled.button`
  margin-top: 22px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 22px;
  border-radius: 10px;
  border: none;
  background: ${({ theme }) => tokens(theme).primary};
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 14px ${orange(0.28)};
  transition: transform 150ms ease, box-shadow 150ms ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px ${orange(0.36)};
  }
`;
